import React, { useState } from "react";
import { get, useFormContext } from "react-hook-form";
import { setNumberValueAs } from "../../../../../../../utils/react-hook-form";
import { FlexRow } from "../../../../../../Common/__styles__/Layout";
import { Label, Select } from "../../../../../../Inputs";
import { Text } from "../../../../../../Inputs/react-hook-form";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
  NumberDynamicDefaultInput,
  NumberStaticDefaultInput,
} from "./shared";

const prefixPostfixOptions = [
  {
    label: "%",
    value: "%",
  },
  {
    label: "$",
    value: "$",
  },
];

const NumberWidgetSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const {
    formState: { errors },
    register,
    setValue,
    getValues,
    watch,
  } = useFormContext<SubmissionsBuilderFormDataStructure>();

  const initialValues = getValues([
    `${fieldPath}.prefix`,
    `${fieldPath}.postfix`,
  ]);

  const [prefixPostfixValue, setPrefixPostfixValue] = useState<
    string | undefined
  >(initialValues[0] || initialValues[1]);

  const setPrefixPostfix = (value?: Maybe<string>) => {
    setPrefixPostfixValue(value ?? undefined);
    switch (value) {
      case "%":
        setValue(`${fieldPath}.prefix`, undefined, { shouldValidate: true });
        setValue(`${fieldPath}.postfix`, value, { shouldValidate: true });
        return;
      case "$":
        setValue(`${fieldPath}.postfix`, undefined, { shouldValidate: true });
        setValue(`${fieldPath}.prefix`, value, { shouldValidate: true });
        return;
      default:
    }
  };

  const isFormulaField = !!watch(`${fieldPath}.formula`);

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={isFormulaField}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <div>
        <Label text={"Placeholder text"} />
        <Text {...register(`${fieldPath}.placeholder`)} disabled={readOnly} />
      </div>
      {!isFormulaField && (
        <div>
          <Text
            {...register(`${fieldPath}.step`, {
              setValueAs: setNumberValueAs,
            })}
            label="Step value"
            type="number"
            error={get(errors, `${fieldPath}.step.message`, "")}
            disabled={readOnly}
          />
        </div>
      )}
      <Select
        name={`${fieldPath}.prefixPostfix`}
        options={prefixPostfixOptions}
        value={prefixPostfixValue}
        onChange={value => setPrefixPostfix(value)}
        label="Format"
        disabled={isModuleInput || readOnly}
      />
      <FlexRow style={{ gap: "16px" }}>
        <Text
          {...register(`${fieldPath}.minimum`, {
            setValueAs: setNumberValueAs,
            deps: [`${fieldPath}.maximum`],
          })}
          label="Number minimum"
          type="number"
          error={get(errors, `${fieldPath}.minimum.message`, "")}
          disabled={readOnly}
        />
        <Text
          {...register(`${fieldPath}.maximum`, {
            setValueAs: setNumberValueAs,
            deps: [`${fieldPath}.minimum`],
          })}
          label="Number maximum"
          type="number"
          error={get(errors, `${fieldPath}.maximum.message`, "")}
          disabled={readOnly}
        />
      </FlexRow>
      <DefaultValuesSection showTooltip>
        <FieldWrapper data-testid="dynamic-default-value-wrapper">
          <NumberDynamicDefaultInput
            fieldPath={fieldPath}
            readOnly={readOnly}
          />
        </FieldWrapper>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <NumberStaticDefaultInput fieldPath={fieldPath} readOnly={readOnly} />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

NumberWidgetSettings.displayName = "NumberWidgetSettings";

export default NumberWidgetSettings;
